/** Entrance / exit sampling. Independent of any one template. */

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

export function easeOutCubic(t) {
  const x = clamp(t, 0, 1);
  return 1 - (1 - x) ** 3;
}

export function easeOutBack(t) {
  const x = clamp(t, 0, 1);
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (x - 1) ** 3 + c1 * (x - 1) ** 2;
}

export function easeOutQuart(t) {
  const x = clamp(t, 0, 1);
  return 1 - (1 - x) ** 4;
}

function mix(a, b, t) {
  return a + (b - a) * t;
}

export function sampleEntrance(preset, t, energetic = false) {
  const p = String(preset || 'popIn');
  if (p === 'none') {
    return { opacity: 1, scale: 1, tx: 0, ty: 0, rotate: 0, blur: 0 };
  }
  const ease = p === 'bounceIn' || p === 'popIn' ? easeOutBack(t) : easeOutCubic(t);
  const sharp = easeOutQuart(t);
  const base = { opacity: mix(0, 1, sharp), scale: 1, tx: 0, ty: 0, rotate: 0, blur: mix(8, 0, sharp) };
  if (p === 'popIn') {
    return { ...base, scale: mix(0.9, 1, ease), ty: mix(20, 0, ease) };
  }
  if (p === 'slideUp') return { ...base, ty: mix(36, 0, ease) };
  if (p === 'slideLeft') return { ...base, tx: mix(40, 0, ease) };
  if (p === 'slideRight') return { ...base, tx: mix(-40, 0, ease) };
  if (p === 'zoomIn') return { ...base, scale: mix(0.72, 1, ease) };
  if (p === 'floatIn') return { ...base, ty: mix(16, 0, ease), scale: mix(0.96, 1, ease) };
  if (p === 'bounceIn') return { ...base, scale: mix(0.82, 1, ease), ty: mix(28, 0, ease) };
  if (p === 'flipIn') return { ...base, rotate: mix(-12, 0, ease), scale: mix(0.92, 1, ease) };
  if (p === 'blurIn') return { ...base, blur: mix(14, 0, sharp), scale: mix(0.97, 1, ease) };
  if (energetic) return { ...base, scale: mix(0.86, 1, ease), ty: mix(24, 0, ease) };
  return { ...base, scale: mix(0.9, 1, ease), ty: mix(20, 0, ease) };
}

export function sampleExit(preset, t) {
  const p = String(preset || 'fadeOut');
  if (p === 'none') return { opacity: 1, scale: 1, tx: 0, ty: 0, rotate: 0, blur: 0 };
  const e = easeOutCubic(t);
  if (p === 'slideDown') return { opacity: mix(1, 0, e), scale: 1, tx: 0, ty: mix(0, 24, e), rotate: 0, blur: 0 };
  if (p === 'zoomOut') return { opacity: mix(1, 0, e), scale: mix(1, 0.88, e), tx: 0, ty: 0, rotate: 0, blur: 0 };
  return { opacity: mix(1, 0, e), scale: 1, tx: 0, ty: 0, rotate: 0, blur: 0 };
}

export function sampleCardMotion({
  localTime = 0,
  clipDuration = 2,
  animationIn = 'popIn',
  animationOut = 'fadeOut',
  animationDuration = 0.4,
  clipOpacity = 1,
  alternatingDirection = false,
  index = 0,
} = {}) {
  const dur = Math.max(0.15, Number(animationDuration) || 0.4);
  const hold = Math.max(dur + 0.05, Number(clipDuration) || dur);
  const t = Math.max(0, Number(localTime) || 0);
  let preset = animationIn;
  if (alternatingDirection) {
    const cycle = ['slideLeft', 'slideUp', 'slideRight', 'floatIn'];
    preset = cycle[index % cycle.length];
  }
  if (t <= dur) {
    const motion = sampleEntrance(preset, t / dur);
    return { ...motion, opacity: motion.opacity * clipOpacity };
  }
  const outStart = hold - dur;
  if (animationOut && animationOut !== 'none' && t >= outStart) {
    const motion = sampleExit(animationOut, (t - outStart) / dur);
    return { ...motion, opacity: motion.opacity * clipOpacity };
  }
  return { opacity: clipOpacity, scale: 1, tx: 0, ty: 0, rotate: 0, blur: 0 };
}

export function staggerStartTimes({
  playhead = 0,
  count = 1,
  staggerDelay = 0.25,
  holdDuration = 2.4,
} = {}) {
  const n = Math.max(1, Math.min(20, Number(count) || 1));
  const delay = Math.max(0, Number(staggerDelay) || 0);
  const start = Math.max(0, Number(playhead) || 0);
  const hold = Math.max(0.4, Number(holdDuration) || 2.4);
  return Array.from({ length: n }, (_, i) => ({
    startTime: start + i * delay,
    duration: hold,
  }));
}

export function motionToCss(motion) {
  const m = motion || {};
  const opacity = Number.isFinite(m.opacity) ? m.opacity : 1;
  const scale = Number.isFinite(m.scale) ? m.scale : 1;
  const tx = Number(m.tx) || 0;
  const ty = Number(m.ty) || 0;
  const rotate = Number(m.rotate) || 0;
  const blur = Math.max(0, Number(m.blur) || 0);
  return {
    opacity,
    transform: `translate(${tx}px, ${ty}px) scale(${scale}) rotate(${rotate}deg)`,
    filter: blur > 0.2 ? `blur(${blur}px)` : 'none',
  };
}
